import React, { useState, useEffect } from 'react';
import RadiologyViewer from './RadiologyViewer';
import StackRadiologyViewer from './StackRadiologyViewer';
import * as cornerstone from "@cornerstonejs/core";

/**
 * A wrapper component that chooses between RadiologyViewer and StackRadiologyViewer
 * based on whether GPU acceleration is available.
 */
const RadiologyViewerWrapper = (props) => {
  const [useFallback, setUseFallback] = useState(false);
  const [checking, setChecking] = useState(true);
  
  // Add a console error monitor to detect GPU issues at runtime
  useEffect(() => {
    // Save the original console methods
    const originalConsoleError = console.error;
    const originalConsoleLog = console.log;
    const originalConsoleWarn = console.warn;
    
    // Function to check for GPU-related messages
    const checkForGPUIssues = (message) => {
      if (typeof message !== 'string') return false;
      
      // Skip the "CornerstoneRender: using GPU rendering" message as it's a positive signal
      if (message.includes("using GPU rendering")) {
        return false;
      }
      
      return (
        message.includes("GPU not detected") ||
        message.includes("using CPU rendering") ||
        message.includes("CPU fallback") ||
        message.includes("VolumeViewports cannot be used") ||
        message.includes("fallback rendering") ||
        message.includes("CornerstoneRender: GPU not detected")
      );
    };
    
    // Function to handle GPU-related messages
    const handleGPUIssue = () => {
      console.log("GPU-related issue detected, setting localStorage flag for future loads");
      localStorage.setItem('useCPUFallback', 'true');
      
      // If we're not already using fallback, switch to it
      if (!useFallback) {
        setUseFallback(true);
      }
    };
    
    // Override console.error
    console.error = (...args) => {
      // Call the original console.error
      originalConsoleError.apply(console, args);
      
      // Check if any arguments contain GPU-related messages
      if (args.some(arg => checkForGPUIssues(String(arg)))) {
        handleGPUIssue();
      }
    };
    
    // Override console.log
    console.log = (...args) => {
      // Call the original console.log
      originalConsoleLog.apply(console, args);
      
      // Check if any arguments contain GPU-related messages
      if (args.some(arg => checkForGPUIssues(String(arg)))) {
        handleGPUIssue();
      }
    };
    
    // Override console.warn
    console.warn = (...args) => {
      // Call the original console.warn
      originalConsoleWarn.apply(console, args);
      
      // Check if any arguments contain GPU-related messages
      if (args.some(arg => checkForGPUIssues(String(arg)))) {
        handleGPUIssue();
      }
    };
    
    // Clean up the overrides when the component unmounts
    return () => {
      console.error = originalConsoleError;
      console.log = originalConsoleLog;
      console.warn = originalConsoleWarn;
    };
  }, [useFallback]);
  
  useEffect(() => {
    // Clear any existing CPU fallback flags that might have been set previously
    if (localStorage.getItem('useCPUFallback') === 'true') {
      console.log('Removing previous CPU fallback setting to try GPU rendering');
      localStorage.removeItem('useCPUFallback');
    }
    
    const checkGPUSupport = async () => {
      try {
        // Method 1: Check WebGL
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        
        if (!gl) {
          console.log('WebGL not supported, using CPU fallback');
          setUseFallback(true);
          setChecking(false);
          return;
        }
        
        // Method 2: Check for specific limitations indicating software rendering
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
          console.log('WebGL renderer:', renderer);
          
          // Look for terms that indicate software rendering
          // Apple Metal on M1/M2/M3 chips should use GPU rendering
          if (
            renderer.toLowerCase().includes('swiftshader') || 
            renderer.toLowerCase().includes('software') || 
            renderer.toLowerCase().includes('llvmpipe') ||
            renderer.toLowerCase().includes('mesa') ||
            // Don't flag ANGLE or Apple Metal as software rendering
            // renderer.toLowerCase().includes('angle') ||
            // (renderer.toLowerCase().includes('apple') && renderer.toLowerCase().includes('metal'))
            
            // Only flag ANGLE without Metal as software rendering
            (renderer.toLowerCase().includes('angle') && 
             !renderer.toLowerCase().includes('metal'))
          ) {
            console.log('Software rendering detected, using CPU fallback');
            setUseFallback(true);
            localStorage.setItem('useCPUFallback', 'true');
            setChecking(false);
            return;
          }
          
          // Special case for Apple Metal - it should use GPU
          if (renderer.toLowerCase().includes('apple') && 
              renderer.toLowerCase().includes('metal')) {
            console.log('Apple Metal GPU detected, using GPU rendering');
          }
        }
        
        // Method 3: Check localStorage for any previous GPU detection issues
        if (localStorage.getItem('useCPUFallback') === 'true') {
          console.log('Previous GPU detection issues found, using CPU fallback');
          setUseFallback(true);
          setChecking(false);
          return;
        }
        
        // No issues detected, use the normal viewer
        setUseFallback(false);
        setChecking(false);
      } catch (error) {
        console.error('Error detecting GPU support:', error);
        // If there's an error during detection, fall back to CPU mode to be safe
        setUseFallback(true);
        setChecking(false);
      }
    };

    checkGPUSupport();
  }, []);

  // Direct check of Cornerstone GPU status
  useEffect(() => {
    const checkCornerstoneGPU = async () => {
      try {
        // First initialize Cornerstone if not already initialized
        if (!cornerstone.isCornerstoneInitialized()) {
          await cornerstone.init();
        }
        
        // Check Cornerstone's internal GPU status
        // This property might not exist in all versions, so we use try/catch
        try {
          const engine = cornerstone.getRenderingEngine();
          if (!engine) {
            console.log('No rendering engine available yet, skipping GPU check');
            return;
          }
          
          const renderingBackend = engine.renderingBackend;
          if (renderingBackend && renderingBackend.usesCPURendering) {
            console.log('Cornerstone using CPU rendering detected');
            setUseFallback(true);
            localStorage.setItem('useCPUFallback', 'true');
          }
        } catch (err) {
          // If we can't directly check, fall back to our other detection methods
          console.log('Could not directly check Cornerstone GPU status:', err);
        }
      } catch (err) {
        console.warn('Error checking Cornerstone GPU status:', err);
      }
    };
    
    // Run the check
    if (!useFallback) {
      checkCornerstoneGPU();
    }
  }, [useFallback]);

  // If we're still checking, show a loading indicator
  if (checking) {
    return (
      <div style={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#000'
      }}>
        <div>Detecting rendering capabilities...</div>
      </div>
    );
  }
  
  // Choose the appropriate viewer based on GPU support
  if (useFallback) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <StackRadiologyViewer {...props} />
      </div>
    );
  } else {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <ViewerErrorBoundary
          onError={(error) => {
            if (
              error.message.includes("GPU not detected") ||
              error.message.includes("using CPU rendering") ||
              error.message.includes("CPU fallback") ||
              error.message.includes("VolumeViewports cannot be used") ||
              error.message.includes("fallback rendering")
            ) {
              console.log("GPU-related error caught in boundary, setting localStorage flag");
              localStorage.setItem('useCPUFallback', 'true');
              setUseFallback(true);
            }
          }}
          fallback={<StackRadiologyViewer {...props} />}
        >
          <RadiologyViewer {...props} />
        </ViewerErrorBoundary>
      </div>
    );
  }
};

// Simple error boundary component
class ViewerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default RadiologyViewerWrapper; 